/**
 * SmartFunnelRouter — V141.4 A/B split-test traffic router.
 *
 * URL: /start (and SK alias /zacni)
 *
 * Decision logic:
 *   1. `?variant=hero|empathetic` query param wins (manual override for testing)
 *   2. `?source=meta|instagram|facebook|tiktok` → empathetic
 *   3. `?source=linkedin|twitter|x|b2b` → heroic
 *   4. localStorage `ab_funnel_variant_v1` (sticky — returning visitor)
 *   5. Random 50/50 split (newcomer)
 *
 * Once assigned, the variant is **sticky** for the visitor — no
 * mid-funnel re-assignment which would muddy conversion data. The
 * variant is logged to `/api/concierge/track` so the admin can see
 * variant-vs-variant conversion in `/api/concierge/stats?compare=variant`.
 */
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../config";

const STORAGE_KEY = "ab_funnel_variant_v1";
const SOURCE_MAP = {
  meta: "empathetic", instagram: "empathetic", facebook: "empathetic",
  fb: "empathetic", tiktok: "empathetic", pinterest: "empathetic",
  linkedin: "heroic", twitter: "heroic", x: "heroic", b2b: "heroic",
  google: "empathetic",
};
const VARIANT_TO_PATH = {
  empathetic: "/prenajmi-si-agenta",
  heroic: "/stan-sa-hrdinom",
};

function pickVariant(searchParams) {
  // 1. Explicit ?variant=
  const explicit = (searchParams.get("variant") || "").toLowerCase();
  if (VARIANT_TO_PATH[explicit]) {
    return { variant: explicit, reason: "explicit_param" };
  }
  // 2. ?source= mapped
  const source = (searchParams.get("source") || searchParams.get("utm_source") || "").toLowerCase();
  if (source && SOURCE_MAP[source]) {
    return { variant: SOURCE_MAP[source], reason: `source_${source}` };
  }
  // 3. Sticky from localStorage
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && VARIANT_TO_PATH[stored]) {
      return { variant: stored, reason: "sticky" };
    }
  } catch (e) { /* localStorage blocked */ }
  // 4. Random 50/50
  return {
    variant: Math.random() < 0.5 ? "empathetic" : "heroic",
    reason: "random_split",
  };
}

export default function SmartFunnelRouter() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const { variant, reason } = pickVariant(searchParams);
    try { localStorage.setItem(STORAGE_KEY, variant); } catch (e) {}
    axios.post(`${API}/concierge/track`, {
      event: "variant_assigned",
      trigger: variant,
      path: "/start",
      // We piggy-back `reason` via trigger field separator. Backend
      // schema doesn't have a dedicated `reason` column yet; this
      // keeps the wire format stable.
    }).catch(() => {});
    // Preserve the rest of the query string (utm_*, coupon, etc.) so
    // attribution + coupon prefills survive the redirect.
    const params = new URLSearchParams(searchParams);
    params.delete("variant"); params.delete("source");
    const qs = params.toString();
    const target = VARIANT_TO_PATH[variant] + (qs ? `?${qs}` : "");
    navigate(target, { replace: true });
    // Also store the reason in sessionStorage so the destination page
    // can read it if it wants to show "(routed via meta-ads)" debug.
    try { sessionStorage.setItem("ab_funnel_reason", reason); } catch (e) {}
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="smart-router-loading">
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-gray-400 uppercase tracking-widest font-black">Pripravujem pre teba...</p>
      </div>
    </div>
  );
}
